import type { CosmicHeaderColumn } from '@/lib/cms/cosmic/types/CosmicHeaderType'
import type {
  CosmicProductType,
  CosmicVariationType,
} from '@/lib/cms/cosmic/types/CosmicProductType'
import { useGTM } from '@/lib/hooks/google-analytics/useGTM'
import useParseLinkUrlWithLocale from '@/lib/hooks/routing/useParseLinkUrlWithLocale'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import { trackShoSelectProduct } from '@/lib/services/mixpanel/events/productEvents'
import { cn } from '@/lib/utils'
import useGlobalStore from '@/store/useGlobalStore'

type Props = {
  columns: [CosmicHeaderColumn, CosmicHeaderColumn?, CosmicHeaderColumn?]
}

const DesktopMenuColumns = ({ columns }: Props) => {
  const { parseLinkUrlWithLocale } = useParseLinkUrlWithLocale()
  const { gtm } = useGTM()
  const {
    user: { customerType },
    app: { marketExceptions },
  } = useGlobalStore()

  return (
    <div className={`grid-cols-${columns.length} pointer-events-auto grid`}>
      {columns.map((column) => (
        <div key={`${column?.title}`}>
          <span className="text-sm text-secondary">{column?.title}</span>
          <div
            className={cn(
              'mt-5 flex flex-col gap-4',
              // column?.bold && 'text-lg font-bold', // TODO: Cosmic does not return the bold property
            )}
          >
            {column?.items?.map((item) => {
              const isProductLink = item.href.includes('/product/')

              return (
                <a
                  onClick={async (event) => {
                    event.preventDefault()

                    gtm.navbarMenuItemClicked({
                      action: item.title,
                      category: column.title,
                    })

                    await mixpanelService
                      .trackEvent(
                        MixpanelEvent.NAVIGATION_BAR_SHOP_MENU_ITEM_CLICKED,
                        {
                          action:
                            (item.href.at(-1) === '/'
                              ? item.href.split('/').at(-2)
                              : item.href.split('/').at(-1)) || '',
                          category: column.title,
                        },
                      )
                      .catch(() => {})

                    if (isProductLink) {
                      const productForTracking: CosmicProductType = {
                        name: item.title,
                        slug:
                          item.href.split('/').pop() ||
                          item.title.toLowerCase().replaceAll(/\s+/g, '-'),
                        description: '',
                        variations: [
                          {
                            sku: '',
                            combinationName: item.title,
                            pricing: null,
                            isDefault: true,
                            isDisable: false,
                            partsLabel: '',
                          } as CosmicVariationType,
                        ],
                        attributes: [],
                        availableFor: {
                          customer: true,
                          member: true,
                          associate: true,
                          employee: true,
                        },
                      }

                      await trackShoSelectProduct({
                        product: productForTracking,
                        selectedVariation: productForTracking.variations[0],
                        eventLocation: 'navigation_bar',
                        action: 'navigation_bar_shop_menu_link_clicked',
                        customerType,
                        currencyCode: marketExceptions?.currencyCode || 'USD',
                      }).catch((error) => {
                        console.error('Product link tracking failed:', error)
                      })
                    }

                    window.location.href = parseLinkUrlWithLocale({
                      url: item.href,
                    })
                  }}
                  className="text-primary no-underline hover:underline"
                  key={item.title}
                  href={parseLinkUrlWithLocale({ url: item.href })}
                >
                  {item.title}
                </a>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

export default DesktopMenuColumns
